import { useState, useEffect, useCallback } from 'react';
import { Loader2, ChevronDown, Pencil, Check, X, Plus, AlertCircle, Shield } from 'lucide-react';
import { bankBalanceApi } from '../../services/bankBalanceApi';
import { useToast } from '../ui/Toast';
import type { BankAccountConfig, SyncCheckResult, UpdateBankConfigData } from '../../types/bankBalance';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS } from '../../types/bankBalance';

interface BankAccountsTabProps {
    companyCode: string | null;
    canEdit: boolean;
    syncCheck: SyncCheckResult | null;
    onRefreshSyncCheck: () => void;
}

export function BankAccountsTab({ companyCode, canEdit, syncCheck, onRefreshSyncCheck }: BankAccountsTabProps) {
    const { toast } = useToast();
    const [configs, setConfigs] = useState<BankAccountConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterCompany, setFilterCompany] = useState<string>(companyCode || '');
    const [filterType, setFilterType] = useState<string>('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editData, setEditData] = useState<UpdateBankConfigData>({});
    const [saving, setSaving] = useState(false);
    // Track inline classify form state per BankID
    const [classifyingId, setClassifyingId] = useState<string | null>(null);
    const [classifyType, setClassifyType] = useState<string>('Account');
    const [classifyCash, setClassifyCash] = useState(true);

    const COMPANIES = ['bin', 'dbi', 'dch', 'dfr', 'dit', 'dpt', 'dsp', 'dtr'];

    const fetchConfigs = useCallback(async () => {
        try {
            setLoading(true);
            const data = await bankBalanceApi.listConfig(filterCompany || undefined);
            setConfigs(data);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load bank accounts', 'error');
        } finally {
            setLoading(false);
        }
    }, [filterCompany, toast]);

    useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

    const handleStartEdit = (config: BankAccountConfig) => {
        setEditingId(config.BankID);
        setEditData({
            bankAccountName: config.BankAccountName,
            bankCompanyName: config.BankCompanyName,
            accountType: config.AccountType,
            isCashRelevant: config.IsCashRelevant,
            isActive: config.IsActive,
            creditLimit: config.CreditLimit,
            interestRate: config.InterestRate,
            maturityDate: config.MaturityDate?.split('T')[0] || null,
            renewalType: config.RenewalType,
            notes: config.Notes,
        });
    };

    const handleSaveEdit = async () => {
        if (!editingId) return;
        setSaving(true);
        try {
            await bankBalanceApi.updateConfig(editingId, editData);
            toast('Bank account updated', 'success');
            setEditingId(null);
            await fetchConfigs();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleClassifyNew = async (bankId: string, bankAccountName: string, company: string, dataSource: string) => {
        try {
            await bankBalanceApi.createConfig({
                companyCode: company,
                bankId,
                bankAccountName,
                bankCompanyName: bankAccountName.split(' ')[0] || 'Unknown',
                accountType: classifyType,
                dataSource,
                isCashRelevant: classifyCash,
            });
            toast(`Classified ${bankAccountName}`, 'success');
            setClassifyingId(null);
            setClassifyType('Account');
            setClassifyCash(true);
            // Refresh both the sync check (parent) and the config list
            await Promise.allSettled([
                fetchConfigs(),
                new Promise<void>(resolve => { onRefreshSyncCheck(); resolve(); }),
            ]);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to classify', 'error');
        }
    };

    const filteredConfigs = configs.filter(c =>
        (!filterType || c.AccountType === filterType)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <select
                        value={filterCompany}
                        onChange={e => setFilterCompany(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Companies</option>
                        {COMPANIES.map(c => (
                            <option key={c} value={c}>{c.toUpperCase()}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative">
                    <select
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Types</option>
                        {ACCOUNT_TYPES.map(t => (
                            <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <span className="text-sm text-gray-500">{filteredConfigs.length} accounts</span>
            </div>

            {/* Unclassified accounts */}
            {syncCheck && syncCheck.unclassifiedCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-3">
                        <AlertCircle className="w-4 h-4" />
                        {syncCheck.unclassifiedCount} new account{syncCheck.unclassifiedCount > 1 ? 's' : ''} to classify
                    </div>
                    <div className="space-y-2">
                        {syncCheck.unclassified.map(u => {
                            const isClassifying = classifyingId === u.BankID;
                            return (
                                <div key={u.BankID} className="bg-white rounded-lg border border-amber-200 px-3 py-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded">NEW</span>
                                            <span className="text-sm font-medium">{u.BankAccountName}</span>
                                            <span className="text-xs text-gray-500">{u.CompanyCode.toUpperCase()} · {u.CurrencyCode} · {u.DataSource}</span>
                                        </div>
                                        {canEdit && !isClassifying && (
                                            <button
                                                onClick={() => { setClassifyingId(u.BankID); setClassifyType('Account'); setClassifyCash(true); }}
                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
                                            >
                                                <Plus className="w-3.5 h-3.5" /> Classify
                                            </button>
                                        )}
                                    </div>
                                    {isClassifying && (
                                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-amber-100">
                                            <div className="relative">
                                                <select
                                                    value={classifyType}
                                                    onChange={e => setClassifyType(e.target.value)}
                                                    className="appearance-none border border-gray-300 rounded px-2 py-1 pr-7 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                >
                                                    {ACCOUNT_TYPES.map(t => (
                                                        <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="absolute right-1.5 top-1.5 w-3 h-3 text-gray-400 pointer-events-none" />
                                            </div>
                                            <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={classifyCash}
                                                    onChange={e => setClassifyCash(e.target.checked)}
                                                    className="rounded"
                                                />
                                                Cash Relevant
                                            </label>
                                            <button
                                                onClick={() => handleClassifyNew(u.BankID, u.BankAccountName, u.CompanyCode, u.DataSource)}
                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                            >
                                                <Check className="w-3.5 h-3.5" /> Save
                                            </button>
                                            <button
                                                onClick={() => setClassifyingId(null)}
                                                className="p-1 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Config table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 font-medium text-gray-600 w-12">Co.</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600">Bank Account</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600">Bank</th>
                                <th className="text-center px-3 py-2 font-medium text-gray-600 w-14">CCY</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 w-32">Type</th>
                                <th className="text-center px-3 py-2 font-medium text-gray-600 w-14">Cash</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 w-20">Source</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-600 w-28">Credit Limit</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-600 w-16">Rate %</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 w-28">Maturity</th>
                                {canEdit && <th className="text-center px-3 py-2 font-medium text-gray-600 w-16">Edit</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredConfigs.map(config => {
                                const isEditing = editingId === config.BankID;

                                return (
                                    <tr
                                        key={config.BankID}
                                        className={`border-b border-gray-100 ${!config.IsActive ? 'opacity-50' : ''} ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                    >
                                        <td className="px-3 py-2 text-xs text-gray-500 uppercase">{config.CompanyCode}</td>
                                        <td className="px-3 py-2">
                                            {isEditing ? (
                                                <input
                                                    value={editData.bankAccountName || ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, bankAccountName: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                />
                                            ) : (
                                                <span className="font-medium">{config.BankAccountName}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {isEditing ? (
                                                <input
                                                    value={editData.bankCompanyName || ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, bankCompanyName: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                />
                                            ) : (
                                                <span className="text-gray-600">{config.BankCompanyName}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center text-gray-500">{config.CurrencyCode}</td>
                                        <td className="px-3 py-2">
                                            {isEditing ? (
                                                <select
                                                    value={editData.accountType || ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, accountType: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                >
                                                    {ACCOUNT_TYPES.map(t => (
                                                        <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                    config.AccountType === 'Account' ? 'bg-blue-100 text-blue-700' :
                                                    config.AccountType === 'CallDeposit' ? 'bg-green-100 text-green-700' :
                                                    config.AccountType === 'TimeDeposit' ? 'bg-purple-100 text-purple-700' :
                                                    config.AccountType === 'FixedTermLoan' ? 'bg-orange-100 text-orange-700' :
                                                    config.AccountType === 'CreditLine' ? 'bg-cyan-100 text-cyan-700' :
                                                    config.AccountType === 'IntercompanyLoan' ? 'bg-gray-100 text-gray-700' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {ACCOUNT_TYPE_LABELS[config.AccountType] || config.AccountType}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            {isEditing ? (
                                                <input
                                                    type="checkbox"
                                                    checked={editData.isCashRelevant ?? false}
                                                    onChange={e => setEditData(prev => ({ ...prev, isCashRelevant: e.target.checked }))}
                                                    className="rounded"
                                                />
                                            ) : (
                                                config.IsCashRelevant ? (
                                                    <Shield className="w-3.5 h-3.5 text-green-600 mx-auto" />
                                                ) : null
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-500">{config.DataSource}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editData.creditLimit ?? ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, creditLimit: e.target.value ? parseFloat(e.target.value) : null }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                                                    placeholder="—"
                                                />
                                            ) : (
                                                config.CreditLimit ? new Intl.NumberFormat('de-CH').format(config.CreditLimit) : '—'
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    value={editData.interestRate ?? ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, interestRate: e.target.value ? parseFloat(e.target.value) : null }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                                                    placeholder="—"
                                                />
                                            ) : (
                                                config.InterestRate ? `${config.InterestRate}%` : '—'
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    value={editData.maturityDate || ''}
                                                    onChange={e => setEditData(prev => ({ ...prev, maturityDate: e.target.value || null }))}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                />
                                            ) : (
                                                config.MaturityDate ? new Date(config.MaturityDate).toLocaleDateString('de-CH') : '—'
                                            )}
                                        </td>
                                        {canEdit && (
                                            <td className="px-3 py-2 text-center">
                                                {isEditing ? (
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={handleSaveEdit}
                                                            disabled={saving}
                                                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingId(null)}
                                                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleStartEdit(config)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
